import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Plus, MagnifyingGlass, FileText, Receipt, CurrencyInr, Bell,
} from "@phosphor-icons/react";
import { toast } from "sonner";

function fmt(n) { return Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 }); }

function daysSince(dateStr) {
  if (!dateStr) return 0;
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return 0;
  const today = new Date();
  return Math.max(0, Math.floor((today - d) / (1000 * 60 * 60 * 24)));
}

export default function Invoices() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("all");
  const [month, setMonth] = useState("");
  const [mineOnly, setMineOnly] = useState(false);
  const [reminderBusy, setReminderBusy] = useState(null); // invoice id while sending
  const [recentReminders, setRecentReminders] = useState({}); // {invoice_id: {days_ago, cooldown_days_remaining}}

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [inv, st, rem] = await Promise.all([
        api.get("/invoices", { params: { search: search || undefined, status, month: month || undefined, mine: mineOnly || undefined } }),
        api.get("/invoices/stats"),
        api.get("/invoices/reminders/recent").catch(() => ({ data: {} })),
      ]);
      setRows(inv.data);
      setStats(st.data);
      setRecentReminders(rem.data || {});
    } catch (err) {
      console.error("Failed to load invoices:", err);
    } finally { setLoading(false); }
  }, [search, status, month, mineOnly]);

  useEffect(() => {
    const t = setTimeout(load, 200);
    return () => clearTimeout(t);
  }, [load]);

  const downloadExcel = () => {
    // Generate CSV from current rows
    const header = ["Invoice","Date","Customer","Sub Total","Taxable","SGST","CGST","Shipping","Total","Status"];
    const lines = rows.map((r) => {
      const c = r.calculation || {};
      return [
        r.invoice_number, r.date, r.customer?.name || "",
        c.sub_total ?? 0, c.taxable_value ?? 0, c.sgst ?? 0, c.cgst ?? 0,
        c.shipping ?? 0, c.total ?? 0, r.status,
      ].map((v) => `"${String(v).replace(/"/g, '""')}"`).join(",");
    });
    const csv = [header.join(","), ...lines].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url; link.download = `invoices-${new Date().toISOString().slice(0,10)}.csv`;
    link.click(); URL.revokeObjectURL(url);
    toast.success("Exported");
  };

  const sendReminder = async (e, inv) => {
    e.stopPropagation();
    const to = inv.customer?.email;
    if (!to) {
      toast.error(`No email on file for ${inv.customer?.name || "this customer"}`);
      return;
    }
    setReminderBusy(inv.id);
    try {
      const { data } = await api.post(`/invoices/${inv.id}/reminder`, {});
      const overdueLabel = data.days_overdue > 0
        ? ` (${data.days_overdue} day${data.days_overdue === 1 ? "" : "s"} overdue)`
        : "";
      toast.success(`Reminder sent to ${to}${overdueLabel}`, {
        description: data.warning || undefined,
        duration: data.warning ? 8000 : 4000,
      });
      // Update the cooldown map immediately so the button reflects state without a full reload
      setRecentReminders((prev) => ({
        ...prev,
        [inv.id]: {
          sent_at: new Date().toISOString(),
          days_ago: 0,
          email_id: data.email_id,
          cooldown_days_remaining: 7,
        },
      }));
    } catch (err) {
      const msg = err?.response?.data?.detail || "Failed to send reminder";
      toast.error(msg);
    } finally {
      setReminderBusy(null);
    }
  };

  return (
    <div data-testid="invoices-page">
      <div className="glass-header sticky top-0 z-10 px-8 py-5">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div>
            <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500">Billing</div>
            <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">Invoices</h1>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={downloadExcel} className="inline-flex items-center gap-2 px-3 h-10 rounded-md border border-slate-200 bg-white text-slate-700 text-sm font-medium hover:bg-slate-50" data-testid="export-invoices-csv">
              Export CSV
            </button>
            <button onClick={() => navigate("/invoices/new")} className="inline-flex items-center gap-2 px-4 h-10 rounded-md bg-blue-600 text-white text-sm font-medium hover:bg-blue-700" data-testid="new-invoice-button">
              <Plus size={16} weight="bold" /> New Invoice
            </button>
          </div>
        </div>
      </div>

      <div className="p-8 space-y-5">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-white border border-slate-200 rounded-lg p-6">
            <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500">Total Revenue (12 mo)</div>
            <div className="mt-3 font-heading text-3xl font-bold text-slate-900">₹{fmt(stats?.total || 0)}</div>
          </div>
          <div className="bg-white border border-slate-200 rounded-lg p-6">
            <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500">Invoices Issued</div>
            <div className="mt-3 font-heading text-3xl font-bold text-slate-900">{stats?.total_invoices || 0}</div>
          </div>
          <div className="bg-white border border-slate-200 rounded-lg p-6">
            <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500">This Month</div>
            <div className="mt-3 font-heading text-3xl font-bold text-slate-900">
              ₹{fmt(stats?.months?.find((m) => m.month === new Date().toISOString().slice(0,7))?.total || 0)}
            </div>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by invoice number or customer…" className="pl-9 h-10 bg-white border-slate-200" data-testid="invoice-search" />
          </div>
          <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="w-44 h-10 bg-white border-slate-200" data-testid="month-filter" />
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="w-44 h-10 bg-white border-slate-200" data-testid="status-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              <SelectItem value="issued">Issued</SelectItem>
              <SelectItem value="draft">Draft</SelectItem>
            </SelectContent>
          </Select>
          <button
            onClick={() => setMineOnly((v) => !v)}
            className={`inline-flex items-center gap-1.5 h-10 px-3 rounded-md border text-sm font-medium transition-colors ${
              mineOnly
                ? "border-blue-200 bg-blue-50 text-blue-700"
                : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50"
            }`}
            data-testid="invoices-mine-toggle"
            title={mineOnly ? "Showing only invoices I created" : "Show all invoices"}
          >
            <span className={`w-2 h-2 rounded-full ${mineOnly ? "bg-blue-600" : "bg-slate-300"}`} />
            Mine only
          </button>
        </div>

        <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
          <table className="w-full text-sm" data-testid="invoices-table">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr className="text-left text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">
                <th className="px-6 py-3">Invoice</th>
                <th className="px-6 py-3">Customer</th>
                <th className="px-6 py-3">Date</th>
                <th className="px-6 py-3 text-right">Total</th>
                <th className="px-6 py-3">Status</th>
                <th className="px-6 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading && (<tr><td colSpan={6} className="px-6 py-10 text-center text-slate-500">Loading…</td></tr>)}
              {!loading && rows.length === 0 && (<tr><td colSpan={6} className="px-6 py-12 text-center text-slate-500">No invoices yet. Click &quot;New Invoice&quot; to create one.</td></tr>)}
              {!loading && rows.map((inv) => {
                const isUnpaid = inv.status === "issued" && !inv.paid;
                const overdueDays = isUnpaid ? daysSince(inv.date) : 0;
                return (
                <tr key={inv.id} onClick={() => navigate(`/invoices/${inv.id}`)} className="cursor-pointer hover:bg-slate-50" data-testid={`invoice-row-${inv.id}`}>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 bg-slate-100 rounded-md flex items-center justify-center text-slate-500"><Receipt size={18} weight="duotone" /></div>
                      <div>
                        <div className="font-mono text-slate-900 font-semibold">{inv.invoice_number}</div>
                        <div className="text-xs text-slate-500">{inv.items?.length || 0} item(s)</div>
                        {inv.created_by_email && (
                          <div
                            className="text-[10px] text-slate-400 truncate max-w-[160px]"
                            title={`Created by ${inv.created_by_email}`}
                            data-testid={`inv-created-by-${inv.id}`}
                          >
                            by {inv.created_by_email.split("@")[0]}
                          </div>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="font-medium text-slate-900">{inv.customer?.name}</div>
                    <div className="text-xs text-slate-500">{inv.customer?.type}</div>
                  </td>
                  <td className="px-6 py-4 text-slate-700">{inv.date}</td>
                  <td className="px-6 py-4 text-slate-900 font-semibold text-right">₹{fmt(inv.calculation?.total)}</td>
                  <td className="px-6 py-4">
                    <div className="flex flex-col gap-1">
                      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold w-fit ${inv.status === "issued" ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"}`}>
                        {inv.status}
                      </span>
                      {inv.paid && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-emerald-600 text-white w-fit">
                          PAID
                        </span>
                      )}
                      {isUnpaid && overdueDays > 0 && (
                        <span className="text-[10px] text-rose-600 font-medium">
                          {overdueDays}d outstanding
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 text-right">
                    {isUnpaid ? (() => {
                      const cooldown = recentReminders[inv.id];
                      if (cooldown) {
                        const label = cooldown.days_ago === 0
                          ? "Reminded today"
                          : `Reminded ${cooldown.days_ago}d ago`;
                        const remaining = cooldown.cooldown_days_remaining ?? 0;
                        return (
                          <span
                            className="inline-flex items-center gap-1.5 px-2.5 h-8 rounded-md border border-slate-200 bg-slate-50 text-slate-500 text-xs font-medium cursor-not-allowed"
                            title={`Cooldown: ${remaining} day${remaining === 1 ? "" : "s"} remaining`}
                            data-testid={`reminder-cooldown-${inv.id}`}
                          >
                            <Bell size={12} weight="bold" />
                            {label}
                          </span>
                        );
                      }
                      return (
                        <button
                          onClick={(e) => sendReminder(e, inv)}
                          disabled={reminderBusy === inv.id}
                          className="inline-flex items-center gap-1.5 px-2.5 h-8 rounded-md border border-rose-200 bg-rose-50 text-rose-700 text-xs font-medium hover:bg-rose-100 disabled:opacity-50 disabled:cursor-not-allowed"
                          data-testid={`reminder-btn-${inv.id}`}
                        >
                          <Bell size={12} weight="bold" />
                          {reminderBusy === inv.id ? "Sending…" : "Send reminder"}
                        </button>
                      );
                    })() : (
                      <span className="text-slate-300 text-xs">—</span>
                    )}
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
